import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

type Line = { id: number; code: string; name: string; isActive: boolean };

export default function Lines() {
  const qc = useQueryClient();
  const [code, setCode] = useState('');
  const [name, setName] = useState('');

  const list = useQuery<{ lines: Line[] }>({
    queryKey: ['admin', 'lines'],
    queryFn: () => api('/api/admin/lines'),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['admin', 'lines'] });

  const create = useMutation({
    mutationFn: (body: { code: string; name: string }) =>
      api('/api/admin/lines', { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: () => {
      setCode('');
      setName('');
      void invalidate();
    },
  });

  const toggle = useMutation({
    mutationFn: ({ id, isActive }: { id: number; isActive: boolean }) =>
      api(`/api/admin/lines/${id}`, { method: 'PATCH', body: JSON.stringify({ isActive }) }),
    onSuccess: invalidate,
  });

  return (
    <div className="space-y-6">
      <section className="bg-card rounded-xl border p-6">
        <h2 className="mb-4 text-lg font-semibold">Nouvelle ligne</h2>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (code.trim() && name.trim()) create.mutate({ code: code.trim(), name: name.trim() });
          }}
          className="flex flex-wrap items-end gap-3"
        >
          <label className="space-y-1.5">
            <span className="block text-sm font-medium">Code</span>
            <input
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="VAL"
              maxLength={16}
              className="border-input bg-background h-11 w-32 rounded-lg border px-3 font-mono"
            />
          </label>
          <label className="space-y-1.5">
            <span className="block text-sm font-medium">Nom du fournisseur</span>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Valvoline"
              className="border-input bg-background h-11 w-64 rounded-lg border px-3"
            />
          </label>
          <button
            type="submit"
            disabled={create.isPending}
            className="bg-primary text-primary-foreground h-11 rounded-lg px-5 font-medium disabled:opacity-50"
          >
            Ajouter
          </button>
        </form>
        {create.isError && (
          <p className="text-destructive mt-3 text-sm font-medium">{(create.error as Error).message}</p>
        )}
      </section>

      <section className="bg-card rounded-xl border p-6">
        <h2 className="mb-4 text-lg font-semibold">Lignes</h2>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-muted-foreground text-left">
              <th className="pb-2 font-medium">Code</th>
              <th className="pb-2 font-medium">Nom</th>
              <th className="pb-2 font-medium">État</th>
              <th className="pb-2" />
            </tr>
          </thead>
          <tbody>
            {list.data?.lines.map((l) => (
              <tr key={l.id} className="border-t">
                <td className="py-3 font-mono font-medium">{l.code}</td>
                <td className="py-3">{l.name}</td>
                <td className="py-3">
                  {l.isActive ? (
                    <span className="text-green-700 dark:text-green-400">Active</span>
                  ) : (
                    <span className="text-muted-foreground">Désactivée</span>
                  )}
                </td>
                <td className="py-3 text-right">
                  <button
                    onClick={() => toggle.mutate({ id: l.id, isActive: !l.isActive })}
                    className="border-input rounded-lg border px-3 py-1.5 text-sm"
                  >
                    {l.isActive ? 'Désactiver' : 'Réactiver'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="text-muted-foreground mt-4 text-xs">
          Désactiver une ligne la retire du périmètre de tous les reps immédiatement. Les commandes
          déjà passées ne sont pas touchées.
        </p>
      </section>
    </div>
  );
}
